import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Button } from '@/components/Elements';
import { DEFAULT_LANG } from '@/config';
import { Country } from '@/features/countries';
import { AppLanguages, BaseFormEntity } from '@/types';
import {
  checkPhone,
  getInputStyleClasses,
  renderField,
  renderLegalCheck,
  renderSelect,
} from '@/utils/forms';
import { getMDirectorSupportPhone } from '@/utils/helpers';

export type iFormContactGuessUserFields = {
  userFirstName: string;
  userLastName: string;
  email: string;
  countryId: string;
  phone: string;
  howManyEmails: number | undefined;
  howManySMS: number | undefined;
  description: string;
  legalTerms: boolean;
  origin: string;
  language: string;
  referer: string;
} & BaseFormEntity;

const initialValuesDefault: iFormContactGuessUserFields = {
  userFirstName: '',
  userLastName: '',
  email: '',
  countryId: '',
  phone: '',
  howManyEmails: undefined,
  howManySMS: undefined,
  description: '',
  legalTerms: false,
  origin: '',
  language: '',
  referer: '',
  formName: 'ContactGuessUser',
};

export type FormContactGuessUserProps = {
  className?: string;
  initialValues?: iFormContactGuessUserFields | undefined;
  onSubmit: (values: iFormContactGuessUserFields) => void;
  origin: string;
  countries: Country[];
};

const renderLabel = (language: string, item: Country) => {
  language = language.toUpperCase();
  return (item.name as any)[language];
};

export const FormContactGuessUser = ({
  onSubmit,
  initialValues,
  className,
  origin,
  countries,
}: FormContactGuessUserProps) => {
  const { t, i18n } = useTranslation();

  const onDefaultSubmit = (values: iFormContactGuessUserFields) => {
    onSubmit && onSubmit(values);
  };

  initialValuesDefault.origin = origin;
  initialValuesDefault.language = i18n.language;
  initialValuesDefault.referer = window.location.href;

  const formik = useFormik({
    initialValues: {
      ...initialValuesDefault,
      ...initialValues,
    },
    validationSchema: Yup.object({
      userFirstName: Yup.string().required(t('Forms.required')),
      userLastName: Yup.string().required(t('Forms.required')),
      email: Yup.string().email(t('Forms.errorEmail')).required(t('Forms.required')),
      phone: Yup.string()
        .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
        .test('test-phone', t('Forms.invalidFormat'), checkPhone)
        .required(t('Forms.required')),
      origin: Yup.string().required(t('Forms.required')),
      language: Yup.string().required(t('Forms.required')),
      referer: Yup.string().required(t('Forms.required')),
      howManyEmails: Yup.string().required(t('Forms.required')),
      howManySMS: Yup.string().required(t('Forms.required')),
      description: Yup.string()
        .min(3, `${t('Forms.minErrorChars')} 3 ${t('Forms.chars')}`)
        .max(200, `${t('Forms.maxErrorChars')} 200 ${t('Forms.chars')}`)
        .required(t('Forms.textRequired')),
      legalTerms: Yup.boolean().oneOf([true], t('Forms.legalRequired')),
    }),
    onSubmit: onDefaultSubmit,
  });

  const getHowManyEmails = () => {
    const options = [];
    for (let x = 1; x < 6; x++) {
      options.push({
        name: t(`Forms.ContactGuessUser.howManyEmailsSteps.step${x}.title`),
        value: t(`Forms.ContactGuessUser.howManyEmailsSteps.step${x}.value`),
      });
    }

    return options;
  };

  const getHowManySMS = () => {
    const options = [];
    for (let x = 1; x < 7; x++) {
      options.push({
        name: t(`Forms.ContactGuessUser.howManySMSSteps.step${x}.title`),
        value: t(`Forms.ContactGuessUser.howManySMSSteps.step${x}.value`),
      });
    }

    return options;
  };

  return (
    <form onSubmit={formik.handleSubmit} className={className}>
      {renderField(formik, 'origin', { type: 'hidden', value: origin })}
      {renderField(formik, 'language', { type: 'hidden' })}
      {renderField(formik, 'referer', { type: 'hidden' })}
      <div className="">
        <h3 className="md__payments__form--h3">{t('Forms.ContactGuessUser.guessTitle')}</h3>
        <p className="mt-2 text-center text-lg lg:text-xl lg:w-80 m-auto">
          {t('Forms.ContactGuessUser.guessSubtitle')}
          <span className="text-md-purple font-black ml-2 block lg:inline-block">
            {getMDirectorSupportPhone()}
          </span>
        </p>
      </div>

      <div className="mt-6 mb-2 flex flex-wrap md:flex-nowrap justify-between gap-3">
        {renderField(formik, 'userFirstName', { customClasses: 'w-full md:w-1/2', required: true })}
        {renderField(formik, 'userLastName', { customClasses: 'w-full md:w-1/2', required: true })}
      </div>

      <div className="mb-2">{renderField(formik, 'email', { type: 'email', required: true })}</div>

      <div className="mb-2 flex flex-wrap md:flex-nowrap justify-between gap-3">
        {renderSelect(formik, 'countryId', {
          customClasses: 'w-full md:w-1/2 bg-md-white',
          itemClasses: 'bg-md-white',
          options: countries,
          renderLabel: (item: Country) => renderLabel(i18n.language, item),
        })}
        {renderSelect(formik, 'phone', {
          customClasses: 'w-full md:w-1/2',
          type: 'phone',
          required: true,
          renderLabel: (item: { name: string }) => item.name,
          defaultCountry: (initialValues?.countryId || i18n.language).toUpperCase(),
          userLanguage: (i18n.language || DEFAULT_LANG) as AppLanguages,
        })}
      </div>

      <div className="mb-2 flex flex-wrap md:flex-nowrap justify-between gap-3">
        {renderSelect(formik, 'howManyEmails', {
          customClasses: 'w-full md:w-1/2',
          itemClasses: 'bg-md-white',
          options: getHowManyEmails(),
          renderLabel: (item: { name: string }) => item.name,
        })}
        {renderSelect(formik, 'howManySMS', {
          customClasses: 'w-full md:w-1/2',
          itemClasses: 'bg-md-white',
          options: getHowManySMS(),
          renderLabel: (item: { name: string }) => item.name,
        })}
      </div>

      <div className="mb-2">
        <div className="font-medium">
          <label htmlFor="description">{t('Forms.ContactGuessUser.description')}*</label>
        </div>
        <textarea
          id="description"
          name="description"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={getInputStyleClasses(
            !!(formik.touched.description && formik.errors.description),
            'textarea'
          )}
          value={formik.values.description}
          rows={6}
        />
        {formik.touched.description && formik.errors.description ? (
          <div className="text-md-red text-xs mt-[-16px]">{formik.errors.description}</div>
        ) : null}
      </div>

      {renderLegalCheck(formik, 'legalTerms', {
        required: true,
        renderLabel: () =>
          t('LegalTerms.checkAccept')
            .replace('{{privacyPolicyURL}}', t('LegalTerms.privacyPolicyURL'))
            .replace('{{cookiesPolicyURL}}', t('LegalTerms.cookiesPolicyURL')),
      })}

      <div className="mb-4 lg:mb-0 flex justify-center">
        <Button variant="primary" size="free" type="submit" className="px-2 py-4 lg:py-2">
          {t('Forms.requestInfo')}
        </Button>
      </div>
    </form>
  );
};
